import { property, state } from 'lit/decorators.js';
import { getApiFactory } from '../api/api-injector';
import {
  DealerDeploymentOperationType,
  DeploymentType,
  TenantInitializationState
} from '../api/api-interface-licenseserver';
import { html, TemplateResult } from 'lit';
import {
  WebModuleLitTable,
  WebModuleLitTableColumnDef
} from '../../webmodule-common/components/src/components/table/lit-table';

import {
  DataEntryPageControlView,
  PageControlTabWithIndependantSaving
} from '../../webmodule-common/other/ui/data-entry-screen-base';
import { constructAsync } from '../../webmodule-common/other/async-constructor';
import {
  MenuIconOption,
  PageButtonLocation,
  PageControl,
  PageControlOptions,
  PageManager
} from '../../webmodule-common/other/ui/page-control';
import { autoElement } from '../../webmodule-common/components/src/webmodule-components';
import { LitElementBase } from '../../webmodule-common/other/litelement-base';
import { DataContainer, DealerDeploymentSupplierFlatView, DealerDeploymentTenantFlatView } from './data-container';

@autoElement()
export class DeploymentsViewTenantList extends LitElementBase {
  @property({ type: Object })
  public data!: DataContainer;
  @property({ type: Number })
  public deploymentId!: number;
  @property({ type: Number })
  public globalSupplierId?: number;
  @state()
  private tableColumns: WebModuleLitTableColumnDef[] = [];
  @state()
  private tableData: DealerDeploymentTenantFlatView[] = [];

  firstUpdated() {
    this.tableData = this.data.flatTenantView(this.deploymentId, this.globalSupplierId);
  }

  constructor() {
    super();
    this.tableColumns = [
      {
        title: () => html`<span class="">Tenant Id</span>`,
        classes: 'colpxmax-125',
        fieldName: 'tenant.id',
        displayValue: (_table: WebModuleLitTable, item: DealerDeploymentTenantFlatView) => {
          return html`<span>${item.tenant.id}</span>`;
        }
      },
      {
        title: () => html`<span class="">Name</span>`,
        classes: 'colpx-250',
        fieldName: 'tenant.companyName',
        displayValue: (_table: WebModuleLitTable, item: DealerDeploymentTenantFlatView) => {
          return html`<span>${item.tenant.companyName}</span>`;
        }
      },
      {
        title: () => html`<span class="">State</span>`,
        classes: 'colpxmax-120',
        fieldName: 'tenantConfig.state',
        displayValue: (_table: WebModuleLitTable, item: DealerDeploymentTenantFlatView) => {
          return html`<span>${TenantInitializationState[item.tenantConfig.state]}</span>`;
        }
      },
      {
        title: () => html`<span class="">Licenses</span>`,
        classes: 'colpxmax-120',
        fieldName: 'tenantConfig.maxLicensePoolAllocation',
        displayValue: (_table: WebModuleLitTable, item: DealerDeploymentTenantFlatView) => {
          return html`<span>${item.tenantConfig.maxLicensePoolAllocation}</span>`;
        }
      }
    ];
  }

  render() {
    return html` <div>
      <webmodule-lit-table
        id="table"
        class="data-table"
        .rowClass=${'row'}
        .colClass=${'col'}
        .data=${[...this.tableData]}
        .columns=${this.tableColumns}
        pageLength=${10000}
        ?clickrows=${false}
      >
      </webmodule-lit-table>
    </div>`;
  }
}

@autoElement()
export class DeploymentsView extends DataEntryPageControlView {
  private supplierDeploymentTab?: SupplierDeploymentTab;

  constructor() {
    super();
  }

  public async afterConstruction(): Promise<void> {
    this.supplierDeploymentTab = await constructAsync(new SupplierDeploymentTab());

    await super.afterConstruction();
    this.pageControl.pageButtonLocation = PageButtonLocation.right;
  }

  public getTitle() {
    return 'Deployments';
  }

  public async setActiveTabByHash() {
    await this.pageControl.applyWindowHash();
  }

  createPageControl(): PageControl {
    // build static pages for each of the configured table settings
    const getInitialPageManagers = (): PageManager[] => {
      const pages: PageManager[] = [];

      if (this.supplierDeploymentTab) pages.push(this.supplierDeploymentTab.createPageManager());

      return pages;
    };
    const menu: MenuIconOption[] = [];
    const options: PageControlOptions = {
      defaultTabIndex: 0,
      menuIcons: menu,
      pageInitializer: () => getInitialPageManagers()
    };
    return new PageControl(options);
  }

  async prepareForSave(): Promise<void> {
    //DOES NOTHING HANDLED BY CHILDREN
  }

  public internalDataChanged(): boolean {
    return false;
  }
}

@autoElement()
export class SupplierDeploymentTab extends PageControlTabWithIndependantSaving {
  @state()
  private data: DataContainer | null = null;
  @state()
  private tableColumns: WebModuleLitTableColumnDef[];
  @state()
  private tableData: DealerDeploymentSupplierFlatView[] = [];

  public allowDeletePage(): boolean {
    return false;
  }

  async prepareForSave(): Promise<void> {
    //do nothing. there is no save based feautre
    //
  }

  public internalDataChanged(): boolean {
    return false;
  }

  getDeploymentTypeBadge(deploymentType: DeploymentType): TemplateResult {
    let badgeText = '';
    let badgevariant = 'primary';
    switch (deploymentType) {
      case DeploymentType.SingleSupplierInterface:
      case DeploymentType.SupplierInterface:
        badgevariant = 'neutral';
        badgeText = 'Supplier Portal';
        break;
      default:
        badgeText = 'Dealer Portal';
        badgevariant = 'primary';
    }
    return html` <webmodule-badge .variant=${badgevariant}
      ><span style="text-align:center; width:100px;">${badgeText}</span></webmodule-badge
    >`;
  }

  getOperationTypeBadge(deploymentType: DealerDeploymentOperationType): TemplateResult {
    const badgeText = DealerDeploymentOperationType[deploymentType];
    let badgevariant = 'primary';
    switch (deploymentType) {
      case DealerDeploymentOperationType.Development:
        badgevariant = 'primary';
        break;
      case DealerDeploymentOperationType.Test:
        badgevariant = 'neutral';
        break;
      case DealerDeploymentOperationType.Production:
        badgevariant = 'success';
        break;
    }
    return html` <webmodule-badge .variant=${badgevariant}
      ><span style="text-align:center; width:100px;">${badgeText}</span></webmodule-badge
    >`;
  }

  private itemExtensionGenerator = (item: DealerDeploymentSupplierFlatView) => {
    return html` <div class="deployment-expansion-detail">
      <wm-deploymentsviewtenantlist
        .data=${this.data}
        .deploymentId=${item.dealerDeployment.id}
        .globalSupplierId=${item.globalSupplier.id}
      ></wm-deploymentsviewtenantlist>
    </div>`;
  };

  constructor() {
    super();

    this.tableColumns = [
      {
        title: () =>
          html` <webmodule-icon
            id="quote-item-expand-all-indicator"
            library="fa"
            name="fas-angles-down"
          ></webmodule-icon>`,
        fieldName: 'type',
        classes: 'colpxmax-35',
        displayValue: (_table: WebModuleLitTable, item: DealerDeploymentSupplierFlatView) => {
          const name = _table.isExpanded(item) ? 'fas-caret-up' : 'fas-caret-down';
          return html` <webmodule-icon
            id="quote-item-expand-all-indicator"
            library="fa"
            name=${name}
          ></webmodule-icon>`;
        },
        click: (_e: Event, table: WebModuleLitTable, item: DealerDeploymentSupplierFlatView) => {
          if (table.isExpanded(item)) {
            table.remExtension(item);
          } else {
            table.addExtension(item, this.itemExtensionGenerator);
          }
          return true;
        }
      },
      {
        title: () => html`<span class="">System Type</span>`,
        fieldName: 'type',
        classes: 'colpx-300',
        displayValue: (_table: WebModuleLitTable, item: DealerDeploymentSupplierFlatView) => {
          return html`${this.getOperationTypeBadge(item.dealerDeployment.operationType)}${this.getDeploymentTypeBadge(
            item.dealerDeployment.deploymentType
          )}`;
        }
      },
      {
        title: () => html`<span class="">Website</span>`,
        fieldName: 'type',
        classes: 'colpx-300',
        displayValue: (_table: WebModuleLitTable, item: DealerDeploymentSupplierFlatView) => {
          return html`<span>${item.dealerDeployment.uiDomain}</span>`;
        }
      },

      {
        title: () => html`<span class="">Version</span>`,
        fieldName: 'type',
        classes: 'colpxmax-130',
        displayValue: (_table: WebModuleLitTable, item: DealerDeploymentSupplierFlatView) => {
          return html`<span>${item.deploymentInformation['build']}</span>`;
        }
      },

      {
        title: () => html`<span class="">V6 Version</span>`,
        fieldName: 'type',
        classes: 'colpxmax-130',
        displayValue: (_table: WebModuleLitTable, item: DealerDeploymentSupplierFlatView) => {
          return html`<span>${item.v6VersionBreakdown.v6Version}</span>`;
        }
      },

      {
        title: () => html`<span class="">Frame Config</span>`,
        fieldName: 'type',
        classes: 'colpxmax-140',
        displayValue: (_table: WebModuleLitTable, item: DealerDeploymentSupplierFlatView) => {
          return html`<span>${item.supplierInstanceInformation['frame config version']}</span>`;
        }
      },

      {
        title: () => html`<span class="">Macro</span>`,
        fieldName: 'type',
        classes: 'colpxmax-80',
        displayValue: (_table: WebModuleLitTable, item: DealerDeploymentSupplierFlatView) => {
          return html`<span>${item.v6VersionBreakdown.macroVersion}</span>`;
        }
      },

      {
        title: () => html`<span class="">Licenses</span>`,
        fieldName: 'type',
        classes: 'colpxmax-100',
        displayValue: (_table: WebModuleLitTable, item: DealerDeploymentSupplierFlatView) => {
          return html`<span>${item.licensePool.concurrentUsers}</span>`;
        }
      },

      {
        title: () => html`<span class="">License Avail</span>`,
        fieldName: 'type',
        classes: 'colpxmax-120',
        displayValue: (_table: WebModuleLitTable, item: DealerDeploymentSupplierFlatView) => {
          const totalAllocations = this.data?.sumLicenseAllocations(item.tenantConfig) ?? 0;
          return html`<span>${item.licensePool.concurrentUsers - totalAllocations}</span>`;
        }
      }
    ];
  }

  async reloadData() {
    const api = getApiFactory().licenseServer();
    const data = await api.getFullSupplierNetwork();
    if (data) {
      this.data = new DataContainer(data);
      this.tableData = this.data.flatDataView();
    }
    this.refreshParent();
  }

  async onEnter(): Promise<void> {
    await this.reloadData();
  }

  private beforeRowRenderEvent = (
    e: CustomEvent<{ item: DealerDeploymentSupplierFlatView; index: number; template?: TemplateResult }>
  ) => {
    const details = e.detail;
    let buildTemplate = details.index === 0;
    if (!buildTemplate) {
      const previousValue = this.tableData[details.index - 1];
      buildTemplate = previousValue.globalSupplier !== details.item.globalSupplier;
    }

    if (buildTemplate) {
      details.template = this.getGlobalSupplierTemplate(details.item);
    }
  };

  bodyTemplate() {
    return html` <webmodule-lit-table
      id="table"
      class="data-table"
      .rowClass=${'row'}
      .colClass=${'col'}
      .data=${[...this.tableData]}
      .columns=${this.tableColumns}
      pageLength=${10000}
      ?clickrows=${false}
      @before-row-render=${this.beforeRowRenderEvent}
    >
    </webmodule-lit-table>`;
  }

  private getGlobalSupplierTemplate(item: DealerDeploymentSupplierFlatView) {
    return html` <div class="global-supplier-section">
      <div class="global-supplier-header">Supplier: ${item.globalSupplier.displayName}</div>
    </div>`;
  }
}
